import React, { useState, useRef, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import './NewsletterPage.css';

export default function NewsletterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const resetTimerRef = useRef(null);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim() || !email.trim()) {
      setErrorMsg('Please fill in both fields.');
      setStatus('error');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorMsg('Please enter a valid email address.');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const templateParams = {
        name: name.trim(),
        email: email.trim(),
        date: new Date().toLocaleString(),
      };

      // === 1. Send Admin Notification ===
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_ADMIN_TEMPLATE_ID,
        templateParams,
        {
          publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
        }
      );

      // === 2. Send Welcome Email to Subscriber (Optional but Recommended) ===
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_WELCOME_TEMPLATE_ID,
        {
          to_name: name.trim(),
          to_email: email.trim(),
        },
        {
          publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
        }
      );

      setStatus('success');
      setName('');
      setEmail('');

      // Clear any existing timer before setting a new one
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);

      // Auto-reset form after 6 seconds
      resetTimerRef.current = setTimeout(() => setStatus('idle'), 6000);
    } catch (error) {
      console.error('EmailJS Error:', error);
      setErrorMsg(error.text || 'Failed to subscribe. Please try again.');
      setStatus('error');
    }
  };

  return (
    <div className="page">
      {/* LEFT PHOTO */}
      <div className="left-panel">
        <img src="/test.png" alt="Unity Coding" className="photo" />
      </div>

      {/* RIGHT FORM */}
      <div className="right-panel">
        <div className="right-inner">
          <p className="brand-name">Unity Coding</p>
          <h1 className="form-title">
            Get coding, web & AI content<br />delivered to your inbox.
          </h1>
          <p className="form-sub">
            Join my email list and get my favourite content regularly. Unsubscribe anytime.
          </p>

          {status === 'success' ? (
            <div className="success-section">
              {/* Confetti overlay */}
              <div className="confetti-container" aria-hidden="true">
                {[...Array(30)].map((_, i) => (
                  <div
                    key={i}
                    className="confetti-piece"
                    style={{
                      '--x': `${Math.random() * 100}%`,
                      '--delay': `${Math.random() * 2}s`,
                      '--duration': `${2 + Math.random() * 2}s`,
                      '--size': `${6 + Math.random() * 8}px`,
                      '--color': ['#f4c414','#166534','#e5b31a','#f97316','#3b82f6','#a855f7','#ec4899','#22c55e'][i % 8],
                      '--rotation': `${Math.random() * 360}deg`,
                    }}
                  />
                ))}
              </div>

              <div className="success-card">
                <div className="success-icon">🎉</div>
                <h3>You're in!</h3>
                <p>Welcome to Unity Coding! Check your inbox for a welcome email.</p>
                <button
                  className="reset-btn"
                  onClick={() => {
                    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
                    setStatus('idle');
                  }}
                >
                  Subscribe another person
                </button>
              </div>
            </div>
          ) : (
            <form className="subscribe-form" onSubmit={handleSubmit} noValidate>
              <input
                type="text"
                placeholder="First Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={status === 'loading'}
                autoComplete="given-name"
              />
              <input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status === 'loading'}
                autoComplete="email"
              />

              {status === 'error' && <div className="error-msg">⚠ {errorMsg}</div>}

              <button type="submit" disabled={status === 'loading'}>
                {status === 'loading' ? <span className="spinner" /> : 'Join!'}
              </button>

              <p className="privacy-note">
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">
                  Terms of Service
                </a>{' '}
                apply.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}